import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { FindingKind, FlowSpecDto, FlowSpecStatusKind } from '@ampsec/platform-client';
import { getFindingsList, getGridData } from 'Apis/library';
import { getApi } from 'Src/Api';
import { useApiObjectData } from 'Hooks/useApiObjectData';

/** Minimal row shape used by both findings grids (getFindingsView / insights). */
export interface FindingsEngagementRow {
  id: string;
  kind?: string;
  finding_kind?: string;
  user_id?: string;
}

export interface UseFindingsEngagementActionsOptions {
  /** When true, use POST /flow_specs/match-findings (additive API path for APD-1629). */
  useApiMatching?: boolean;
}

export interface UseFindingsEngagementActionsResult {
  matchingEngagements: string[];
  noOfUsers: number;
  forceTrigger: boolean;
  overrideWorkingHours: boolean;
  onOverrideForceTrigger: (event: ChangeEvent<HTMLInputElement>) => void;
  onOverrideWorkingHours: (event: ChangeEvent<HTMLInputElement>) => void;
}

const flowSpecTransformer = (dataItem: { data?: FlowSpecDto[] }) => dataItem?.data ?? [];

function getRowEventType(row: FindingsEngagementRow): string | undefined {
  return row.kind ?? row.finding_kind;
}

function filterEligibleFlowSpecsClient(
  flowSpecs: FlowSpecDto[],
  eventTypes: string[],
  forceTrigger: boolean
): FlowSpecDto[] {
  const eventTypesSet = new Set(eventTypes.filter(Boolean));
  const matchesTrigger = (fs: FlowSpecDto) =>
    fs.triggerFilter?.trigger?.some((t) => eventTypesSet.has(t.eventType as FindingKind)) ?? false;
  return flowSpecs
    .filter(
      (fs) =>
        fs.status !== FlowSpecStatusKind.ARCHIVE &&
        (forceTrigger || fs.status !== FlowSpecStatusKind.PAUSED)
    )
    .filter(matchesTrigger);
}

/**
 * Encapsulates engagement concerns: flow specs fetch, compliance event-type map,
 * matching flow specs for the selected findings, and override state for the ActionsTray.
 * Grids pass selectedRows and use the returned props when rendering ActionsTray.
 */
export function useFindingsEngagementActions(
  selectedRows: FindingsEngagementRow[],
  options: UseFindingsEngagementActionsOptions = {}
): UseFindingsEngagementActionsResult {
  const { useApiMatching = false } = options;
  const [forceTrigger, setForceTrigger] = useState(false);
  const [overrideWorkingHours, setOverrideWorkingHours] = useState(true);
  const [complianceEventTypeMap, setComplianceEventTypeMap] = useState<Map<string, string>>(new Map());
  const [apiMatchedFlowSpecs, setApiMatchedFlowSpecs] = useState<FlowSpecDto[]>([]);

  const { data: fetchFlowSpecs } = useApiObjectData('flowSpecs', getGridData, flowSpecTransformer, {
    limit: 250,
  }, !useApiMatching);

  useEffect(() => {
    const kind = FindingKind.COMPLIANCE_BENCHMARK_RULE_VIOLATION;
    const fids: string[] =
      selectedRows?.filter((r) => getRowEventType(r) === kind)?.map((r) => r.id) ?? [];
    if (fids.length === 0) {
      setComplianceEventTypeMap(new Map());
      return;
    }
    getFindingsList({
      id: { $in: fids },
      include: ['meta', 'saasComponent'],
    })
      .then((findings) => {
        const map = new Map<string, string>();
        for (const finding of findings) {
          const eventType = _.get(finding.meta, '_raw.eventType');
          if (!_.isEmpty(eventType) && _.isString(eventType)) {
            map.set(finding.id, eventType);
          }
        }
        setComplianceEventTypeMap(map);
      })
      .catch(() => {
        setComplianceEventTypeMap(new Map());
      });
  }, [selectedRows]);

  useEffect(() => {
    if (!useApiMatching || selectedRows.length === 0) {
      setApiMatchedFlowSpecs([]);
      return;
    }
    const findingIds = selectedRows.map((row) => row.id).filter(Boolean);
    if (findingIds.length === 0) {
      setApiMatchedFlowSpecs([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const api = await getApi();
        const matchFindings = (api as AmpApiWithMatchFindings | null)?.flowSpecs?.matchFindings;
        if (!matchFindings) {
          if (!cancelled) {
            setApiMatchedFlowSpecs([]);
          }
          return;
        }
        const entries = await matchFindings({
          findingIds,
          forceTrigger,
        });
        if (cancelled) {
          return;
        }
        const byName = new Map<string, FlowSpecDto>();
        for (const entry of entries) {
          for (const flowSpec of entry.flowSpecs ?? []) {
            byName.set(flowSpec.name, flowSpec);
          }
        }
        setApiMatchedFlowSpecs(Array.from(byName.values()));
      } catch {
        if (!cancelled) {
          setApiMatchedFlowSpecs([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedRows, useApiMatching, forceTrigger]);

  const matchingFlowSpecs = useMemo(() => {
    if (selectedRows.length === 0) {
      return [];
    }
    if (useApiMatching) {
      return apiMatchedFlowSpecs;
    }
    const eventTypes = selectedRows.map((row) => {
      const rowEventType = getRowEventType(row);
      if (rowEventType === FindingKind.COMPLIANCE_BENCHMARK_RULE_VIOLATION) {
        return complianceEventTypeMap.get(row.id ?? '') ?? rowEventType;
      }
      return rowEventType;
    });
    const flowSpecsList = Array.isArray(fetchFlowSpecs) ? fetchFlowSpecs : [];
    return filterEligibleFlowSpecsClient(
      flowSpecsList,
      eventTypes.filter((eventType): eventType is string => Boolean(eventType)),
      forceTrigger
    );
  }, [selectedRows, forceTrigger, complianceEventTypeMap, fetchFlowSpecs, useApiMatching, apiMatchedFlowSpecs]);

  const onOverrideForceTrigger = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setForceTrigger(event.target.checked);
  }, []);

  const onOverrideWorkingHours = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setOverrideWorkingHours(event.target.checked);
  }, []);

  const noOfUsers = useMemo(
    () => _.uniqBy(selectedRows, 'user_id').length,
    [selectedRows]
  );

  return {
    matchingEngagements: matchingFlowSpecs.map((fs) => fs.name),
    noOfUsers,
    forceTrigger,
    overrideWorkingHours,
    onOverrideForceTrigger,
    onOverrideWorkingHours,
  };
}

type FindingsFlowSpecMatchEntry = {
  fid: string;
  flowSpecs: FlowSpecDto[];
};

type AmpApiWithMatchFindings = {
  flowSpecs: {
    matchFindings: (input: { findingIds: string[]; forceTrigger?: boolean }) => Promise<FindingsFlowSpecMatchEntry[]>;
  };
};
